import { Flag, MapPin, Palmtree, PartyPopper, School, type LucideIcon } from 'lucide-react';

import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import type { HolidayType } from '@/types/holiday';
import { HOLIDAY_TYPES } from './schemas/holiday.schema';

/** How the calendar describes each kind of closure. */
export const HOLIDAY_TYPE_LABEL = {
  NATIONAL: 'National holiday',
  FESTIVAL: 'Festival',
  REGIONAL: 'State or district',
  SCHOOL: 'Institute holiday',
  VACATION: 'Vacation',
} as const satisfies Record<HolidayType, string>;

/**
 * Tones are chosen so the two that change what staff do stand out: a vacation
 * is a long stretch (accent) and an institute's own closure is the one somebody
 * here decided (warning). The statutory ones sit quietly.
 */
export const HOLIDAY_TYPE_TONE = {
  NATIONAL: 'info',
  FESTIVAL: 'success',
  REGIONAL: 'neutral',
  SCHOOL: 'warning',
  VACATION: 'accent',
} as const satisfies Record<HolidayType, BadgeTone>;

/** What each kind means, under the picker. */
export const HOLIDAY_TYPE_HINT = {
  NATIONAL: 'Independence Day, Republic Day, Gandhi Jayanti.',
  FESTIVAL: 'Diwali, Eid, Christmas, Pongal.',
  REGIONAL: 'A state or district closure — a local election, a regional new year.',
  SCHOOL: "Your institute's own: founder's day, a sports meet, an unscheduled closure.",
  VACATION: 'A term break: summer, Dussehra, winter.',
} as const satisfies Record<HolidayType, string>;

export const HOLIDAY_TYPE_OPTIONS: Option<HolidayType>[] = HOLIDAY_TYPES.map((value) => ({
  value,
  label: HOLIDAY_TYPE_LABEL[value],
}));

/** The filter row offers the same list, plus everything. */
export const HOLIDAY_TYPE_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'All kinds' },
  ...HOLIDAY_TYPE_OPTIONS,
];

/**
 * How each kind of closure is drawn across the screen.
 *
 * One table rather than a colour decided at each of the four places a kind
 * appears — the tile, the filter chip, the year strip and the card. Five
 * near-identical blocks of markup is how "Festival" ends up gold in one view and
 * orange in another (CLAUDE.md §3).
 *
 * The hues are chosen so a year read at a glance is readable by colour alone: a
 * national holiday takes the console's blue because it is the fixed skeleton
 * every institute shares, and everything the institute chooses for itself sits
 * around it.
 */
export interface HolidayTypeStyle {
  /** The word on a tile, where "National holiday" would wrap. */
  short: string;
  icon: LucideIcon;
  /** The chip a single holiday wears in the strip and on its card. */
  chip: string;
  /** The mark beside it in a legend, and its dot on the year strip. */
  dot: string;
  /** The arc it takes in the ring. */
  stroke: string;
  /** The tile's own tint, matching `StatCard`'s variants. */
  variant: 'indigo' | 'amber' | 'cyan' | 'rose' | 'emerald';
}

export const HOLIDAY_TYPE_STYLE = {
  NATIONAL: {
    short: 'National',
    icon: Flag,
    chip: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900/60',
    dot: 'bg-blue-500',
    stroke: 'stroke-blue-500',
    variant: 'indigo',
  },
  FESTIVAL: {
    short: 'Festivals',
    icon: PartyPopper,
    chip: 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/60',
    dot: 'bg-amber-500',
    stroke: 'stroke-amber-500',
    variant: 'amber',
  },
  REGIONAL: {
    short: 'Regional',
    icon: MapPin,
    chip: 'bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-900/60',
    dot: 'bg-cyan-500',
    stroke: 'stroke-cyan-500',
    variant: 'cyan',
  },
  SCHOOL: {
    short: 'Our own',
    icon: School,
    chip: 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900/60',
    dot: 'bg-rose-500',
    stroke: 'stroke-rose-500',
    variant: 'rose',
  },
  VACATION: {
    short: 'Vacations',
    icon: Palmtree,
    chip: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60',
    dot: 'bg-emerald-500',
    stroke: 'stroke-emerald-500',
    variant: 'emerald',
  },
} as const satisfies Record<HolidayType, HolidayTypeStyle>;

/** The order the year is read in: the fixed skeleton first, the choices after. */
export const HOLIDAY_TYPE_ORDER: HolidayType[] = [
  'NATIONAL',
  'FESTIVAL',
  'REGIONAL',
  'SCHOOL',
  'VACATION',
];

export const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
